import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  HostListener,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  ChatService,
  ChatMessage,
  ChatResponse,
} from '../services/chat.service';
import { SearchService } from '../services/search.service';
import { AuthService } from '../services/auth.service';
import { ParseListingLinksPipe } from '../pipes/parse-listing-links.pipe';
import { Subject, takeUntil, debounceTime } from 'rxjs';

@Component({
  selector: 'app-chat-interface',
  standalone: true,
  imports: [CommonModule, FormsModule, ParseListingLinksPipe],
  templateUrl: './chat-interface.component.html',
  styleUrls: ['./chat-interface.component.css'],
})
export class ChatInterfaceComponent
  implements OnInit, OnDestroy, AfterViewChecked, OnChanges
{
  @Input() isVisible = false;
  @Output() onClose = new EventEmitter<void>();

  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('messageInput') messageInput!: ElementRef;

  private destroy$ = new Subject<void>();
  private shouldScrollToBottom = false;
  private inputChangeSubject = new Subject<Event>();
  private scrollPosition = 0;
  private readonly MOBILE_BREAKPOINT = 430;

  messages: ChatMessage[] = [];
  currentMessage = '';
  isTyping = false;
  showErrorMessage = false;
  errorMessage = '';
  showConfirmDialog = false;
  confirmDialogTitle = '';
  confirmDialogMessage = '';
  confirmDialogActionText = '';
  private confirmAction: (() => void) | null = null;

  quickActions = [
    {
      icon: '🚗',
      text: 'Find BMW',
      message: 'Show me available BMW automobiles',
    },
    {
      icon: '🏠',
      text: 'Browse Apartments',
      message: "I'm looking for any apartments in Skopje",
    },
    {
      icon: '💼',
      text: 'Find Jobs',
      message: 'Help me find job opportunities',
    },
    {
      icon: 'ℹ️',
      text: 'About Markado',
      message: 'Tell me about Markado and how it works',
    },
  ];

  constructor(
    private chatService: ChatService,
    private searchService: SearchService,
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadChatHistory();
    this.initializeSubscriptions();
    this.setupInputDebounce();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible']) {
      if (changes['isVisible'].currentValue) {
        this.lockBodyScroll();
      } else {
        this.unlockBodyScroll();
      }
    }
  }

  ngOnDestroy(): void {
    this.saveChatHistory();
    this.unlockBodyScroll();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  private initializeSubscriptions(): void {
    this.chatService.isThinking$
      .pipe(takeUntil(this.destroy$))
      .subscribe((thinking: boolean) => {
        this.isTyping = thinking;
        if (thinking) {
          this.shouldScrollToBottom = true;
        }
        this.cdr.detectChanges();
      });

    this.chatService.isOnline$
      .pipe(takeUntil(this.destroy$))
      .subscribe((online: boolean) => {
        if (!online && !this.showErrorMessage) {
          this.showError(
            'AI assistant is currently offline. Please try again later.'
          );
        }
      });

    this.authService.isLoggedIn$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoggedIn: boolean) => {
        if (!isLoggedIn) {
          this.clearChatCompletely();
        }
      });
  }

  private setupInputDebounce(): void {
    this.inputChangeSubject
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe((event: Event) => {
        this.adjustTextareaHeight(event);
      });
  }

  private loadChatHistory(): void {
    try {
      const saved = localStorage.getItem('markado-ai-chat-history');
      if (saved) {
        const parsedMessages = JSON.parse(saved);
        this.messages = parsedMessages
          .map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
          }))
          .filter((msg: ChatMessage) => this.isValidMessage(msg));
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
      this.messages = [];
    }
  }

  private saveChatHistory(): void {
    try {
      const historyToSave = this.messages.slice(-50);
      localStorage.setItem(
        'markado-ai-chat-history',
        JSON.stringify(historyToSave)
      );
    } catch (error) {
      console.warn('Failed to save chat history:', error);
    }
  }

  private clearChatCompletely(): void {
    this.messages = [];
    this.currentMessage = '';
    try {
      localStorage.removeItem('markado-ai-chat-history');
      this.chatService.clearChatHistory();
    } catch (error) {
      console.warn('Failed to clear chat history:', error);
    }
  }

  private isValidMessage(msg: any): boolean {
    return (
      msg &&
      typeof msg.role === 'string' &&
      typeof msg.content === 'string' &&
      msg.timestamp
    );
  }

  handleCloseChat(): void {
    if (this.messages.length > 0) {
      this.showConfirmation(
        'End Chat Session?',
        'Are you sure you want to close the chat? This will clear your conversation history.',
        'End Chat',
        () => {
          this.clearChatCompletely();
          this.onClose.emit();
        }
      );
    } else {
      this.onClose.emit();
    }
  }

  handleQuickAction(message: string): void {
    this.currentMessage = message;
    this.handleSendMessage();
  }

  async handleSendMessage(): Promise<void> {
    if (!this.canSendMessage()) return;

    const messageText = this.currentMessage.trim();
    const userMessage: ChatMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    this.messages.push(userMessage);
    this.shouldScrollToBottom = true;
    this.currentMessage = '';
    this.resetTextareaHeight();

    try {
      const response = await this.chatService
        .sendMessage(messageText, this.getConversationHistory())
        .toPromise();

      if (response) {
        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content:
            response.message ||
            'I apologize, but I received an empty response.',
          timestamp: new Date(),
          searchResults: response.search_results,
          compareResults: response.compare_results,
          error: response.error,
          ragEnhanced: response.rag_enhanced,
          ragListingsFound: response.rag_listings_found,
        };

        this.messages.push(assistantMessage);
        this.shouldScrollToBottom = true;
        this.saveChatHistory();
      }
    } catch (error) {
      console.error('Chat error:', error);
      this.handleChatError(error);
    }
  }

  handleClearChat(): void {
    this.showConfirmation(
      'Clear Chat History?',
      'This will permanently delete all messages in this conversation.',
      'Clear Chat',
      () => {
        this.clearChatCompletely();
        this.shouldScrollToBottom = true;
      }
    );
  }

  handleShowHelp(): void {
    const helpMessage: ChatMessage = {
      role: 'assistant',
      content: `**Welcome to Markado AI Assistant!** 🤖

Here are some things you can ask me:

**🔍 Search Examples:**
• "Show me BMW cars under €15,000"
• "Find 2-bedroom apartments in Skopje center"
• "Latest iPhone models for sale"
• "Remote software developer jobs"

**⚖️ Comparison:**
• "Compare listing 123 and listing 456"
• "Show me the differences between these cars"

**ℹ️ Information:**
• "How does Markado work?"
• "What categories are available?"
• "How do I contact a seller?"

**💡 Tips:**
• Be specific with your search terms
• Include price ranges, locations, or preferences
• Ask me to clarify if results aren't what you expected

Just type naturally - I understand conversational language!`,
      timestamp: new Date(),
    };

    this.messages.push(helpMessage);
    this.shouldScrollToBottom = true;
    this.saveChatHistory();
  }

  handleViewListing(listing: any): void {
    if (listing?.subcategory && listing?.id) {
      this.router.navigate([
        '/listing-details',
        listing.subcategory,
        listing.id,
      ]);
      this.onClose.emit();
    } else {
      this.showError('Unable to view listing. Invalid listing data.');
    }
  }

  handleViewAllResults(searchResults: any): void {
    if (searchResults) {
      this.router.navigate(['/search'], {
        queryParams: {
          q: searchResults.query || '',
          category: searchResults.category || 'all',
        },
      });
      this.onClose.emit();
    }
  }

  handleDismissError(): void {
    this.showErrorMessage = false;
    this.errorMessage = '';
  }

  handleCancelConfirm(): void {
    this.showConfirmDialog = false;
    this.confirmAction = null;
  }

  handleConfirmAction(): void {
    this.showConfirmDialog = false;
    if (this.confirmAction) {
      this.confirmAction();
      this.confirmAction = null;
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.handleSendMessage();
    }
  }

  onInputChange(event: Event): void {
    this.inputChangeSubject.next(event);
  }

  private adjustTextareaHeight(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    if (!textarea) return;

    textarea.style.height = 'auto';

    const maxHeight = window.innerWidth <= 768 ? 100 : 120;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);

    textarea.style.height = newHeight + 'px';
    textarea.style.overflowY =
      textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  private resetTextareaHeight(): void {
    if (this.messageInput?.nativeElement) {
      const textarea = this.messageInput.nativeElement;
      textarea.style.height = 'auto';
      textarea.style.overflowY = 'hidden';
    }
  }

  canSendMessage(): boolean {
    const canSend = !!(this.currentMessage?.trim() && !this.isTyping);
    return canSend;
  }

  getStatusText(): string {
    const status = this.isTyping ? 'Typing...' : 'Online';
    return status;
  }

  getDisplayResults(results: any[]): any[] {
    return results?.slice(0, 6) || [];
  }

  getListingImage(listing: any): string {
    if (listing?.first_image) {
      return listing.first_image;
    }
    return this.getDefaultImage(listing?.category || '');
  }

  getDefaultImage(category: string): string {
    const defaultImages: { [key: string]: string } = {
      vehicles: '/assets/images/categories/vehicles-default.jpg',
      'real-estate': '/assets/images/categories/real-estate-default.jpg',
      marketplace: '/assets/images/categories/marketplace-default.jpg',
      jobs: '/assets/images/categories/jobs-default.jpg',
    };
    return (
      defaultImages[category] ||
      '/assets/images/listing-placeholder/no-image-placeholder.png'
    );
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.src = '/assets/images/listing-placeholder/no-image-placeholder.png';
    }
  }

  formatPrice(price: number): string {
    if (!price) return '0';
    return new Intl.NumberFormat('en-US').format(price);
  }

  formatMessage(content: string): string {
    if (!content) return '';

    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br>')
      .replace(/`(.*?)`/g, '<code>$1</code>');
  }

  formatTime(date: Date): string {
    if (!date) return '';

    try {
      return new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }).format(date);
    } catch {
      return '';
    }
  }

  trackMessage(index: number, message: ChatMessage): string {
    return `${message.role}-${message.timestamp?.getTime() || index}-${index}`;
  }

  private getConversationHistory(): Array<{ role: string; content: string }> {
    return this.messages
      .slice(-10)
      .filter((msg) => msg.role && msg.content)
      .map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));
  }

  private showConfirmation(
    title: string,
    message: string,
    actionText: string,
    action: () => void
  ): void {
    this.confirmDialogTitle = title;
    this.confirmDialogMessage = message;
    this.confirmDialogActionText = actionText;
    this.confirmAction = action;
    this.showConfirmDialog = true;
  }

  private showError(message: string): void {
    this.errorMessage = message;
    this.showErrorMessage = true;
  }

  private handleChatError(error: any): void {
    const errorMessage: ChatMessage = {
      role: 'assistant',
      content: this.getErrorMessage(error),
      timestamp: new Date(),
      error: true,
    };

    this.messages.push(errorMessage);
    this.shouldScrollToBottom = true;
    this.saveChatHistory();
  }

  private getErrorMessage(error: any): string {
    if (
      error?.message?.includes('network') ||
      error?.message?.includes('fetch')
    ) {
      return "I'm having trouble connecting to the server. Please check your internet connection and try again.";
    }

    if (error?.status === 429) {
      return "I'm receiving too many requests right now. Please wait a moment and try again.";
    }

    if (error?.status >= 500) {
      return 'The server is experiencing issues. Please try again in a few minutes.';
    }

    return "I apologize, but I'm having trouble processing your request right now. Please try again.";
  }

  private scrollToBottom(): void {
    try {
      if (this.messagesContainer?.nativeElement) {
        const container = this.messagesContainer.nativeElement;
        container.scrollTop = container.scrollHeight;
      }
    } catch (error) {}
  }

  private lockBodyScroll(): void {
    if (
      typeof window !== 'undefined' &&
      window.innerWidth <= this.MOBILE_BREAKPOINT
    ) {
      this.scrollPosition =
        window.pageYOffset || document.documentElement.scrollTop;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    }
  }

  private unlockBodyScroll(): void {
    if (
      typeof window !== 'undefined' &&
      window.innerWidth <= this.MOBILE_BREAKPOINT
    ) {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, this.scrollPosition);
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.isVisible) {
      event.preventDefault();
      this.handleCloseChat();
    }
  }

  @HostListener('window:beforeunload')
  onBeforeUnload(): void {
    this.saveChatHistory();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isVisible) {
      if (window.innerWidth <= this.MOBILE_BREAKPOINT) {
        this.lockBodyScroll();
      } else {
        this.unlockBodyScroll();
      }
    }
  }
}
